import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Check,
  ChevronDown,
  Clock,
  Filter,
  Mail,
  Phone,
} from 'lucide-react';
import { tenantApi } from '@shared/api/tenantApi';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { pageCopy } from '@shared/lib/pageCopy';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';

interface ManagementContact {
  id: string;
  name: string;
  role?: string;
  email?: string;
  mobile?: string;
  hours?: string;
  profile_image_url?: string;
}

interface ManagementGroup {
  id: string;
  name: string;
  contacts: ManagementContact[];
}

interface ApiResponse<T> {
  success?: boolean;
  data?: T;
}

interface ContactGroup {
  contact_group_id: string;
  name: string;
}

interface Contact {
  contact_id: string;
  title?: string | null;
  first_name?: string | null;
  surname?: string | null;
  email?: string | null;
  heading?: string | null;
  designation?: string | null;
  mobile_number?: string | null;
  home_number?: string | null;
  additional_number?: string | null;
  picture_url?: string | null;
  show_name?: boolean;
  show_email?: boolean;
  show_phone?: boolean;
  member?: { first_name?: string | null; last_name?: string | null; email?: string | null } | null;
}

function unwrapData<T>(response: ApiResponse<T> | T): T {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as ApiResponse<T>).data as T;
  }
  return response as T;
}

function contactName(contact: Contact): string {
  const contactParts = [contact.title, contact.first_name, contact.surname].filter(Boolean);
  if (contact.show_name !== false && contactParts.length > 0) {
    return contactParts.join(' ').trim();
  }

  const memberParts = [contact.member?.first_name, contact.member?.last_name].filter(Boolean);
  if (contact.show_name !== false && memberParts.length > 0) {
    return memberParts.join(' ').trim();
  }

  return contact.heading || contact.member?.email || 'Management contact';
}

function mapContact(contact: Contact, group: ContactGroup): ManagementContact {
  return {
    id: contact.contact_id,
    name: contactName(contact),
    role: contact.designation || group.name,
    email: contact.show_email === false ? undefined : contact.email || undefined,
    mobile: contact.show_phone === false
      ? undefined
      : contact.mobile_number || contact.home_number || contact.additional_number || undefined,
    profile_image_url: contact.picture_url || undefined,
  };
}

async function fetchManagement(): Promise<ManagementGroup[]> {
  const { data: groupsResponse } = await tenantApi.get<ApiResponse<ContactGroup[]>>('/contact-groups/management');
  const groups = unwrapData<ContactGroup[]>(groupsResponse) ?? [];
  return (await Promise.all(
    groups.map(async (group, groupIndex) => {
      const { data: contactsResponse } = await tenantApi.get<ApiResponse<Contact[]>>(
        `/contact-groups/${group.contact_group_id}/contacts/member`,
      );
      return {
        id: group.contact_group_id || `group-${groupIndex}`,
        name: group.name || 'Management',
        contacts: (unwrapData<Contact[]>(contactsResponse) ?? []).map((contact) => mapContact(contact, group)),
      };
    }),
  )).filter((group) => group.contacts.length > 0);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function Management(): JSX.Element {
  const copy = pageCopy().management;
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const managementQuery = useQuery({ queryKey: ['management'], queryFn: fetchManagement });
  const groups = managementQuery.data ?? [];
  const visibleGroups = useMemo(
    () => selectedGroupIds.length === 0
      ? groups
      : groups.filter((group) => selectedGroupIds.includes(group.id)),
    [groups, selectedGroupIds],
  );
  const visibleContacts = useMemo(
    () => visibleGroups.flatMap((group) => (
      group.contacts.map((contact) => ({
        contact,
        categoryName: group.name,
      }))
    )),
    [visibleGroups],
  );

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow="Management"
        title="Estate team."
        subtitle="Board members, estate management and on-site team contacts."
      />

      {groups.length > 0 ? (
        <CategoryFilterDropdown
          groups={groups.map((group) => ({ id: group.id, name: group.name }))}
          open={filtersOpen}
          selectedGroupIds={selectedGroupIds}
          onOpenChange={setFiltersOpen}
          onSelectedGroupIdsChange={setSelectedGroupIds}
        />
      ) : null}

      <div className="flex flex-col gap-3 px-5 pt-4 pb-safe-or-6">
        {managementQuery.isLoading ? (
          <>
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </>
        ) : managementQuery.isError ? (
          <ErrorState onRetry={() => managementQuery.refetch()} />
        ) : visibleContacts.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-7 w-7" strokeWidth={1.5} />}
            title={copy.emptyTitle}
            description={copy.emptyDescription}
          />
        ) : (
          visibleContacts.map(({ contact, categoryName }) => (
            <ManagementContactCard
              key={`${categoryName}-${contact.id}`}
              contact={contact}
              categoryName={categoryName}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface CategoryFilterDropdownProps {
  groups: Array<{ id: string; name: string }>;
  open: boolean;
  selectedGroupIds: string[];
  onOpenChange: (open: boolean) => void;
  onSelectedGroupIdsChange: (ids: string[]) => void;
}

function CategoryFilterDropdown({
  groups,
  open,
  selectedGroupIds,
  onOpenChange,
  onSelectedGroupIdsChange,
}: CategoryFilterDropdownProps): JSX.Element {
  const selectedCount = selectedGroupIds.length;
  const label = selectedCount === 0 ? 'All categories' : `${selectedCount} selected`;

  function toggleGroup(groupId: string): void {
    onSelectedGroupIdsChange(
      selectedGroupIds.includes(groupId)
        ? selectedGroupIds.filter((id) => id !== groupId)
        : [...selectedGroupIds, groupId],
    );
  }

  return (
    <div className="relative px-5 pt-4">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex w-full items-center gap-2 rounded-2xl bg-card px-4 py-3 text-left text-body-sm font-semibold text-ink shadow-card hairline"
        aria-expanded={open}
      >
        <Filter className="h-4 w-4 shrink-0 text-primary-700" strokeWidth={1.75} />
        <span className="min-w-0 flex-1 truncate">Categories</span>
        <span className="shrink-0 text-body-xs font-medium text-ink-secondary">{label}</span>
        <ChevronDown
          className={'h-4 w-4 shrink-0 text-ink-muted transition-transform ' + (open ? 'rotate-180' : '')}
          strokeWidth={1.75}
        />
      </button>

      {open ? (
        <div className="absolute left-5 right-5 top-full z-20 mt-2 max-h-72 overflow-y-auto rounded-2xl bg-card p-2 shadow-elevated hairline">
          <button
            type="button"
            onClick={() => onSelectedGroupIdsChange([])}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-body-sm font-medium text-ink"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
              {selectedCount === 0 ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : null}
            </span>
            All contact groups
          </button>

          {groups.map((group) => {
            const checked = selectedGroupIds.includes(group.id);
            return (
              <button
                key={group.id}
                type="button"
                onClick={() => toggleGroup(group.id)}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-body-sm text-ink"
              >
                <span
                  className={
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-pill ' +
                    (checked ? 'bg-primary-700 text-white' : 'bg-paper hairline')
                  }
                >
                  {checked ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : null}
                </span>
                <span className="min-w-0 flex-1 truncate">{group.name}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ManagementContactCard({
  contact,
  categoryName,
}: {
  contact: ManagementContact;
  categoryName: string;
}): JSX.Element {
  return (
    <Card asChild noGrain className="rounded-2xl p-4">
      <article>
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-pill bg-primary-50">
            {contact.profile_image_url ? (
              <img src={contact.profile_image_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-body-sm font-semibold text-primary-700">
                {initials(contact.name)}
              </span>
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-headline leading-tight text-ink">{contact.name}</p>
            {contact.role ? (
              <p className="text-body-sm text-ink-secondary">{contact.role}</p>
            ) : null}
            <span className="mt-1 inline-flex max-w-full items-center rounded-pill bg-primary-50 px-2 py-0.5 text-[11px] font-semibold leading-4 text-primary-700">
              <span className="truncate">{categoryName}</span>
            </span>
          </div>
          {contact.mobile ? (
            <a
              href={`tel:${contact.mobile}`}
              aria-label={`Call ${contact.name}`}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-primary-700 text-white"
            >
              <Phone className="h-4.5 w-4.5" strokeWidth={1.75} />
            </a>
          ) : null}
        </div>

        <div className="mt-3 flex flex-col gap-1.5">
          {contact.mobile ? (
            <a
              href={`tel:${contact.mobile}`}
              className="flex items-center gap-2 text-body-sm font-semibold text-primary-700"
            >
              <Phone className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              {contact.mobile}
            </a>
          ) : null}
          {contact.email ? (
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-2 text-body-sm text-ink-secondary"
            >
              <Mail className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.5} />
              <span className="truncate">{contact.email}</span>
            </a>
          ) : null}
          {contact.hours ? (
            <p className="flex items-center gap-2 text-body-sm text-ink-muted">
              <Clock className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              {contact.hours}
            </p>
          ) : null}
        </div>
      </article>
    </Card>
  );
}
